import type { Feed, UIComponentType } from '~/services/layout/index.ts';
import type { ServerLoadedFeeds } from '~/services/layout/server-loaded-feeds.ts';

export const TextInputTypes = {
  String: 'string',
  Number: 'number',
  Date: 'date',
} as const;

export type TextInputType =
  (typeof TextInputTypes)[keyof typeof TextInputTypes];

export const FormComponentTypes = {
  Label: 'label',
  TextInput: 'textInput',
  DatePicker: 'datePicker',
  SingleSelect: 'singleSelect',
  MultiSelect: 'multiSelect',
  CheckBox: 'checkBox',
  Button: 'button',
} as const;

export type TextLikeFormType =
  | typeof FormComponentTypes.TextInput
  | typeof FormComponentTypes.DatePicker;

function isTextInputFormType(
  type: unknown
): type is typeof FormComponentTypes.TextInput {
  return type === FormComponentTypes.TextInput;
}

function isDatePickerFormType(
  type: unknown
): type is typeof FormComponentTypes.DatePicker {
  return type === FormComponentTypes.DatePicker;
}

export function isTextLikeFormType(type: unknown): type is TextLikeFormType {
  return isTextInputFormType(type) || isDatePickerFormType(type);
}

function isTextInputType(value: unknown): value is TextInputType {
  return (
    value === TextInputTypes.String ||
    value === TextInputTypes.Number ||
    value === TextInputTypes.Date
  );
}

export function isDateInputType(
  value: unknown
): value is typeof TextInputTypes.Date {
  return value === TextInputTypes.Date;
}

export function isNumberInputType(
  value: unknown
): value is typeof TextInputTypes.Number {
  return value === TextInputTypes.Number;
}

/**
 * Date pickers always resolve to `date`. Text inputs use a stored type when it
 * is `string`, `number`, or `date`, otherwise `string`.
 */
export function resolveTextInputType(input: {
  formType?: unknown;
  inputType?: unknown;
}): TextInputType {
  if (isDatePickerFormType(input.formType)) {
    return TextInputTypes.Date;
  }

  if (isTextInputType(input.inputType)) {
    return input.inputType;
  }

  return TextInputTypes.String;
}

export type FormFieldOption = {
  const: string;
  title: string;
};

type BaseOptions = {
  title: string;
  description?: string;
  required?: boolean;
};

export type BaseFormField = {
  id: string;
  options?: BaseOptions;
  title?: string;
  extensions?: Record<string, unknown>;
};

export type FormTextInputField = BaseFormField & {
  type: typeof FormComponentTypes.TextInput;
  preset: 'FormTextInput';
  options?: BaseOptions & {
    minLength?: number;
    maxLength?: number;
    inputType?: TextInputType;
  };
};

export type FormDatePickerField = BaseFormField & {
  type: typeof FormComponentTypes.DatePicker;
  preset: 'FormTextInput';
  options?: BaseOptions;
};

export type FormCheckBoxField = BaseFormField & {
  type: typeof FormComponentTypes.CheckBox;
  preset: 'FormMultiSelectGroup';
};

export type FormSingleSelectField = BaseFormField & {
  type: typeof FormComponentTypes.SingleSelect;
  preset: 'FormMultiSelectGroup';
  options: BaseOptions & {
    items: FormFieldOption[];
    itemsFeedURL?: string;
  };
};

export type FormMultiSelectField = BaseFormField & {
  type: typeof FormComponentTypes.MultiSelect;
  preset: 'FormMultiSelectGroup';
  options: BaseOptions & {
    items?: FormFieldOption[];
    itemsFeedURL?: string;
  };
};

export type FormButtonField = BaseFormField & {
  type: typeof FormComponentTypes.Button;
  preset: 'FormButtonSave' | 'FormButtonCancel' | 'FormButtonDelete';
  options?: BaseOptions;
};

export type FormLabelField = BaseFormField & {
  preset: '';
  type: typeof FormComponentTypes.Label;
};

export type FormField =
  | FormTextInputField
  | FormDatePickerField
  | FormCheckBoxField
  | FormSingleSelectField
  | FormMultiSelectField
  | FormButtonField
  | FormLabelField;

export type TextLikeFormField = FormTextInputField | FormDatePickerField;

export function isTextLikeField(field: FormField): field is TextLikeFormField {
  return isTextLikeFormType(field.type);
}

export function isTextInputField(
  field: FormField
): field is FormTextInputField {
  return isTextInputFormType(field.type);
}

/** Option A remote form feed shape: direct FormConfig JSON. */
export type FormConfig = {
  properties: FormField[];
};

export type FormValueValidator = (value: string | undefined) => string[];

export type FormUiComponent = {
  id: string;
  name: string;
  component_type: UIComponentType | string;
  data?: Record<string, unknown>;
  position?: number;
  rules?: Record<string, unknown>;
  styles?: Record<string, unknown>;
  advertising?: Record<string, unknown>;
  localizations?: Record<string, unknown>;
  general?: Record<string, unknown>;
  ui_components?: FormUiComponent[];
  addition_styles?: Record<string, string | number | undefined>;
};

export type LayoutPresetsMapping = Record<string, FormUiComponent>;

export type LayoutPresetsInput =
  | LayoutPresetsMapping
  | { presets_mappings?: LayoutPresetsMapping };

export type ComponentSourcesType = {
  dataSource: () => Promise<Feed>;
  validator: FormValueValidator;
  component: FormUiComponent;
  fieldId: string;
};

export type UIComponentsDataSources = Record<string, ComponentSourcesType>;

export type ErrorsValidationResolver = {
  fullyResolved: string[];
  partiallyResolved: string[];
};

export type FormScreenLoaderData = {
  formConfig: FormConfig | null;
  layoutPresetsUsed: LayoutPresetsMapping;
  uiComponents: FormUiComponent[];
  serverLoadedFeeds: ServerLoadedFeeds;
  loadError: string | null;
};
